import {
  Box,
  Button,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  Flex,
  VStack,
  useBoolean,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { FaRegCheckCircle } from 'react-icons/fa';
import { MdClear, MdError } from 'react-icons/md';
import { useMatch, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';

import apiService from 'services';
import colors from 'styles/theme/colors';
import sizes from 'styles/theme/sizes';
import ShareImg from 'assets/share_icon.png';
import AuthModalContainer from 'components/AuthModal/AuthModalContainer';
import FigmaLinkIcon from 'components/Icons/FigmaLinkIcon';
import FigmaAccessIcon from 'components/Icons/FigmaAccessIcon';
import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import FigmaEmbedIcon from 'components/Icons/FigmaEmbedIcon';
import { getSharedModelUsers, getModelUsers } from 'redux/HeaderSlice';
import { getUserData, getUserSubscriptionLimits } from 'redux/UserSlice';
import ShareModalPermission from 'pages/PermissionComponent/ShareModalPermission';
import { AppDispatch } from 'utils/GlobalHelpers';
import { accent, font, neutral, shadow, text as textToken } from 'styles/revampTokens';

import mailboxImage from '../../assets/figma-share-modal/mailbox.svg';

import UserDetails from './UserDetails';

const emailSchema = yup.object().shape({
  email: yup.string().email().min(1),
});

const ShareButtonWithPopup = () => {
  const dispatch: AppDispatch = useDispatch();

  // Only the setter is used — passed to ShareModalPermission so it can flag its
  // own nested "upgrade" modal; the flag's value isn't read here any more now
  // that Chakra's Modal (not a manual outside-click handler) owns closing.
  const [, setUpgrade] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isEmailAddressValid, setIsEmailAddressValid] = useState(true);
  const [modelLink, setModelLink] = useState('');

  const toast = useToast();
  const navigate = useNavigate();
  const user = useSelector(getUserData);
  const [isModalOpen, setIsModalOpen] = useBoolean();
  const match = useMatch({ path: 'model/:modelId/', end: false });

  const modelId = match?.params?.modelId;

  const shareModelUsers = useSelector(getSharedModelUsers);
  const userSubscriptionLimitData = useSelector(getUserSubscriptionLimits);

  const modelOwner = shareModelUsers.find((share: any) => share.role === 'owner');

  const isShareModelAccess = shareModelUsers.find((share: any) => {
    return share?.user_id === user?.id;
  });

  useEffect(() => {
    if (modelId) {
      dispatch(getModelUsers(Number(modelId)));
    }
  }, [modelId]);

  const { refetch: fetchModelLink } = useQuery(
    ['modelLink', modelId],
    () => apiService.get_model_link(Number(modelId)),
    {
      enabled: false,
      onError: () => navigate('/error'),
    },
  );

  // The link itself is only ever shown, not an access-level toggle — the API
  // behind it (`get_model_link`) returns one shareable URL, there's no
  // "anyone with the link can view/edit" setting on the backend to surface.
  useEffect(() => {
    if (isModalOpen && modelId && !modelLink) {
      fetchModelLink().then(({ data }) => {
        const link = data?.data?.link;
        if (link) setModelLink(link);
      });
    }
  }, [isModalOpen, modelId]);

  const { refetch: sendInvite } = useQuery(
    ['modalLinkInvite', modelId],
    () =>
      apiService.create_model_share({
        model_id: Number(modelId),
        request_body: {
          email_id: emailAddress,
        },
      }),
    {
      enabled: false,
      retry: false,
      onError: () => navigate('/error'),
    },
  );

  const emailChangeHandler = (event_: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(event_.target.value.trim());
    setIsEmailAddressValid(true);
  };

  const showToast = (type: 'SUCCESS' | 'ERROR', message: string, duration = 1250) => {
    toast({
      duration,
      isClosable: false,
      render: () => (
        <Box
          position='relative'
          bg={type === 'SUCCESS' ? colors.green[800] : colors.red[500]}
          p={sizes[2]}
          pl={sizes[3]}
          pr={sizes[3]}
          borderRadius={sizes['2']}
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          columnGap={sizes[2]}
        >
          {type === 'SUCCESS' ? (
            <FaRegCheckCircle fontSize='x-large' />
          ) : (
            <MdError fontSize='x-large' />
          )}
          <Text fontSize='sm' mr={'auto'}>
            {message}
          </Text>
          <Box position='absolute' top={sizes[1]} right={sizes['1']}>
            <MdClear fontSize='large' />
          </Box>
        </Box>
      ),
    });
  };

  const sendInviteHandler = async () => {
    const isEmailValid = emailSchema.isValidSync({ email: emailAddress });

    setIsEmailAddressValid(isEmailValid);

    if (isEmailValid && emailAddress && modelId) {
      sendInvite().then(() => {
        showToast('SUCCESS', 'Email Sent Successfully');
        setEmailAddress('');
      });
    }
  };

  const copyToClipboard = async (link: string) => {
    let status: 'COPIED' | 'FAILED' = 'FAILED';
    try {
      await navigator.clipboard.writeText(link);
      status = 'COPIED';
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.append(textArea);
      textArea.select();
      textArea.focus();
      try {
        document.execCommand('copy');
        status = 'COPIED';
      } catch {
        status = 'FAILED';
      } finally {
        textArea.remove();
      }
    }
    showToast(
      status === 'COPIED' ? 'SUCCESS' : 'ERROR',
      status === 'COPIED' ? 'Link Copied' : 'Something Went Wrong',
    );
  };

  // Both actions are gated on `modelLink`, which the effect above fetches as
  // soon as the modal opens — so there's no fetch-then-copy path to handle.
  const copyLinkHandler = () => {
    if (modelLink) copyToClipboard(modelLink);
  };

  /** Copies an iframe snippet so the model can be dropped into a wiki/intranet page. */
  const copyEmbedHandler = async () => {
    if (!modelLink) return;
    copyToClipboard(
      `<iframe src="${modelLink}" width="100%" height="600" frameborder="0" allowfullscreen></iframe>`,
    );
  };

  const onToggleAuthModal = () => {
    setIsAuthModalOpen((previousState) => !previousState);
  };

  const handleShareClick = (event: React.MouseEvent) => {
    if (user?.is_anonymous) {
      setIsAuthModalOpen(true);
      event.stopPropagation();
    } else if (modelId) {
      dispatch(getModelUsers(Number(modelId)));
    }
  };

  return (
    <>
      <Button
        variant='clear'
        size=''
        fontSize='2xs'
        textTransform='uppercase'
        color='black.900'
        padding={'0 .5rem'}
        _hover={{ bgColor: 'green.900', color: 'white' }}
        onClick={(event) => {
          handleShareClick(event);
          if (!user?.is_anonymous) setIsModalOpen.on();
        }}
      >
        Share
        <Image ml={2} h={6} w={6} src={ShareImg} />
      </Button>

      {/* Centered dialog following the Figma "Share your Model" design, sized
          to the revamp's other modals (ModernDeleteConfirmModal, the Scenario
          Comparison dialog) so the type scale and control heights stay
          consistent across the app. */}
      <Modal isOpen={isModalOpen} onClose={setIsModalOpen.off} isCentered scrollBehavior='inside'>
        <ModalOverlay bg='rgba(16, 20, 30, 0.45)' />
        <ModalContent
          maxW='396px'
          w='calc(100vw - 32px)'
          maxH='calc(100vh - 64px)'
          borderRadius='13px'
          boxShadow={shadow.overlay}
          border='1px solid'
          borderColor={neutral.border}
          p={0}
        >
          <ModalHeader p='16px' pb={0}>
            <Flex justify='space-between' align='flex-start' gap={4}>
              <Box flex={1} minW={0}>
                <Text
                  fontFamily={font.display}
                  fontSize='18px'
                  fontWeight='700'
                  lineHeight='normal'
                  color={textToken.primary}
                >
                  Share your Model
                </Text>
                <Text
                  fontFamily={font.body}
                  fontSize='12px'
                  fontWeight={400}
                  lineHeight='17px'
                  color={textToken.secondary}
                  mt='6px'
                >
                  You&apos;ve created a new project! Invite your team to review and collaborate on
                  this model.
                </Text>
              </Box>
              <Image src={mailboxImage} width='84px' height='69px' flexShrink={0} alt='' />
            </Flex>
          </ModalHeader>

          <ModalBody p='16px' display='flex' flexDir='column' gap='16px'>
            <Box>
              <Text
                fontFamily={font.body}
                fontSize='11px'
                fontWeight={600}
                color={textToken.secondary}
                mb='4px'
              >
                Project Link
              </Text>
              <Flex
                align='center'
                justify='space-between'
                gap={3}
                border='1px solid'
                borderColor={neutral.border}
                borderRadius='8px'
                bg={neutral.surface}
                p='10px'
              >
                <Text
                  flex={1}
                  isTruncated
                  fontFamily={font.body}
                  fontSize='12px'
                  fontWeight={500}
                  color={modelLink ? textToken.primary : textToken.muted}
                >
                  {modelLink || 'Generating link…'}
                </Text>
                <Flex align='center' gap='16px' flexShrink={0}>
                  <Flex
                    as='button'
                    type='button'
                    align='center'
                    gap='6px'
                    color={accent.solid}
                    transition='opacity 0.15s ease'
                    _hover={{ opacity: 0.7 }}
                    _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                    disabled={!modelLink}
                    onClick={copyEmbedHandler}
                  >
                    <FigmaEmbedIcon width={13} height={13} color={accent.solid} />
                    <Text fontFamily={font.body} fontSize='12px' fontWeight={500}>
                      Embed
                    </Text>
                  </Flex>
                  <Flex
                    as='button'
                    type='button'
                    align='center'
                    gap='9px'
                    color={textToken.secondary}
                    transition='opacity 0.15s ease'
                    _hover={{ opacity: 0.7 }}
                    _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                    disabled={!modelLink}
                    onClick={copyLinkHandler}
                  >
                    <FigmaLinkIcon width={13} height={13} color={textToken.secondary} />
                    <Text fontFamily={font.body} fontSize='12px' fontWeight={500}>
                      Copy
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Box>

            {userSubscriptionLimitData?.subscription_features_limit?.sharing === 'execute' ? (
              <Flex
                align='center'
                gap='10px'
                bg={accent.subtle}
                border='1px solid'
                borderColor={accent.border}
                borderRadius='10px'
                px='13px'
                h='58px'
              >
                <Flex
                  align='center'
                  justify='center'
                  border='1px solid'
                  borderColor={neutral.border}
                  borderRadius='8px'
                  bg={neutral.surface}
                  flexShrink={0}
                  boxSize='32px'
                >
                  <FigmaAccessIcon width={16} height={16} color={accent.solid} />
                </Flex>
                <Box minW={0}>
                  <Text
                    fontFamily={font.body}
                    fontSize='13px'
                    fontWeight={500}
                    color={textToken.primary}
                  >
                    Restricted access
                  </Text>
                  <Text
                    fontFamily={font.body}
                    fontSize='12px'
                    fontWeight={400}
                    color={textToken.secondary}
                    isTruncated
                  >
                    Only invited users can access this model
                  </Text>
                </Box>
              </Flex>
            ) : (
              <ShareModalPermission modelOwner={modelOwner} user={user} setUpgrade={setUpgrade} />
            )}

            <Box
              opacity={
                userSubscriptionLimitData?.subscription_features_limit?.sharing === 'view' ? 0.4 : 1
              }
            >
              <Text
                fontFamily={font.body}
                fontSize='12px'
                fontWeight={600}
                color={textToken.secondary}
                mb='4px'
              >
                Invite team members
              </Text>
              <Flex
                align='center'
                justify='space-between'
                gap={3}
                border='1px solid'
                borderColor={!isEmailAddressValid ? '#E5484D' : neutral.border}
                borderRadius='8px'
                bg={neutral.surface}
                p='10px'
                transition='border-color 0.15s ease, box-shadow 0.15s ease'
                _focusWithin={{
                  borderColor: accent.solid,
                  boxShadow: `0 0 0 3px ${accent.subtle}`,
                }}
              >
                <Flex align='center' gap='9px' flex={1} minW={0}>
                  <FigmaSearchIcon width={16} height={16} color={accent.solid} />
                  <Input
                    variant='unstyled'
                    type='email'
                    placeholder='Search'
                    fontFamily={font.body}
                    fontSize='13px'
                    color={textToken.primary}
                    _placeholder={{ color: textToken.muted }}
                    onChange={emailChangeHandler}
                    value={emailAddress}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') sendInviteHandler();
                    }}
                    isDisabled={isShareModelAccess?.role === 'view'}
                  />
                </Flex>
                <Flex
                  as='button'
                  type='button'
                  align='center'
                  flexShrink={0}
                  color={accent.solid}
                  transition='opacity 0.15s ease'
                  _hover={{ opacity: 0.7 }}
                  _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                  disabled={!emailAddress || isShareModelAccess?.role === 'view'}
                  onClick={sendInviteHandler}
                >
                  <Text fontFamily={font.body} fontSize='12px' fontWeight={500}>
                    Send Invite
                  </Text>
                </Flex>
              </Flex>
              {!isEmailAddressValid && (
                <Text fontFamily={font.body} color='#E5484D' fontSize='11px' mt='5px'>
                  Please enter a valid email address to share your model
                </Text>
              )}
            </Box>

            <Box>
              <Text
                fontFamily={font.body}
                fontSize='11px'
                fontWeight={600}
                color={textToken.secondary}
                mb='10px'
              >
                People with access
              </Text>
              <VStack w='full' align='stretch' spacing='12px'>
                {shareModelUsers?.map((shareUser: any) => (
                  <UserDetails
                    shareUser={shareUser}
                    key={shareUser.user_id}
                    modelOwner={modelOwner}
                    userPermission={userSubscriptionLimitData?.subscription_features_limit?.sharing}
                  />
                ))}
              </VStack>
            </Box>
          </ModalBody>

          <ModalFooter p='16px' pt={0}>
            {/* `variant='unstyled'`: the app's Button theme defaults to the
                `outlined` variant, which would add a 1px white border and a
                24px radius on top of this filled button. */}
            <Button
              variant='unstyled'
              display='flex'
              alignItems='center'
              justifyContent='center'
              w='full'
              h='40px'
              bg={accent.solid}
              color={textToken.inverse}
              fontFamily={font.body}
              fontWeight={600}
              fontSize='13px'
              borderRadius='10px'
              transition='background 0.15s ease, transform 0.1s ease'
              _hover={{ bg: accent.hover }}
              _active={{ transform: 'scale(0.98)' }}
              onClick={setIsModalOpen.off}
            >
              Dismiss
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AuthModalContainer isOpen={isAuthModalOpen} onClose={onToggleAuthModal} />
    </>
  );
};

export default ShareButtonWithPopup;
